import {
  bootArchipelagoIslands,
  unmountArchipelagoIslands,
} from "@archipelago-js/react"

import registry from "./registry.generated"

// The archipelago library only unmounts React roots on turbo:before-cache
// (full page navigations). When Turbo Streams replace DOM content (e.g.
// new webhook requests streaming in), old island elements are removed from
// the DOM but their React roots stay in the library's internal Map, leaking
// memory. We watch for removed *mounted* island elements and cycle
// unmount+boot so only DOM-present islands retain active React roots.
//
// Caveats addressed:
// - Observer is reconnected on turbo:load since Turbo replaces document.body
// - Only triggers remount when a data-mounted island is actually removed,
//   not on any arbitrary DOM mutation

let remountTimer = null

function scheduleRemount() {
  if (remountTimer) return
  remountTimer = requestAnimationFrame(() => {
    remountTimer = null
    unmountArchipelagoIslands()
    void bootArchipelagoIslands(registry)
  })
}

function hasMountedIsland(node) {
  if (!(node instanceof Element)) return false
  if (node.matches("[data-island][data-mounted]")) return true
  return node.querySelector("[data-island][data-mounted]") != null
}

function cancelPendingRemount() {
  if (remountTimer) {
    cancelAnimationFrame(remountTimer)
    remountTimer = null
  }
}

// --- Observer lifecycle ---

let cleanupObserver = null

function startObserver() {
  if (typeof MutationObserver === "undefined") return

  stopObserver()

  cleanupObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (hasMountedIsland(node)) {
          scheduleRemount()
          return
        }
      }
    }
  })

  const target = document.body ?? document.documentElement
  if (target) {
    cleanupObserver.observe(target, { childList: true, subtree: true })
  }
}

function stopObserver() {
  if (cleanupObserver) {
    cleanupObserver.disconnect()
    cleanupObserver = null
  }
}

// --- Boot ---

void bootArchipelagoIslands(registry)

if (document.body) startObserver()
else document.addEventListener("DOMContentLoaded", startObserver)

document.addEventListener("turbo:before-cache", () => {
  stopObserver()
  cancelPendingRemount()
})

document.addEventListener("turbo:load", () => {
  startObserver()
  void bootArchipelagoIslands(registry)
})
